import { motion } from "framer-motion";
import { Plus, MapPin, Check, Users, Briefcase } from "lucide-react";
import { MockShell, Spot, Typed, DepotPill } from "../Frames";
import { OverviewBody } from "./Overview";

const Field = ({ label, value, on }) => (
  <Spot on={on}>
    <p className="text-[10px] font-semibold text-slate-500 mb-1">{label}</p>
    <div className="h-9 rounded-md border border-slate-200 bg-white px-3 flex items-center text-[12px]">{value}</div>
  </Spot>
);

const SignUp = () => (
  <div className="w-full h-full bg-[#0B1120] home-grid flex items-center justify-center text-left">
    <motion.div initial={{ y: 16, opacity: 0 }} animate={{ y: 0, opacity: 1 }} className="w-[390px] rounded-xl bg-white text-[#0F172A] p-6 space-y-3 shadow-2xl">
      <p className="font-display font-extrabold text-[18px]">Start your company</p>
      <div className="grid grid-cols-2 gap-3">
        <Field label="Company name" value={<Typed text="Southern Cross Plastering" />} on />
        <Field label="First depot" value={<Typed text="North Yard" delay={1.6} />} on />
      </div>
      <Field label="Your name" value="Jess Murray" />
      <Field label="Email" value="jess@southerncross.com.au" />
      <div className="h-10 rounded-full bg-[#EA580C] text-white font-semibold flex items-center justify-center text-[12px]">Create company &amp; open dashboard</div>
    </motion.div>
  </div>
);

const DepotCard = ({ name, jobs, logins, fresh }) => (
  <motion.div initial={fresh ? { opacity: 0, y: 10 } : false} animate={{ opacity: 1, y: 0 }} transition={{ delay: fresh ? 1.2 : 0 }}
    className="rounded-lg border border-slate-200 bg-white p-4">
    <div className="flex items-center gap-2"><MapPin className="w-4 h-4 text-[#EA580C]" /><p className="font-display font-bold text-[14px]">{name}</p></div>
    <div className="mt-3 flex gap-4 text-[11px] text-slate-500">
      <span className="flex items-center gap-1"><Briefcase className="w-3 h-3" />{jobs} jobs</span>
      <span className="flex items-center gap-1"><Users className="w-3 h-3" />{logins} logins</span>
    </div>
  </motion.div>
);

const Depots = () => (
  <MockShell tab="Depots" depot="All depots">
    <p className="font-display font-bold text-[16px] mb-3">Depots</p>
    <Spot on className="mb-4">
      <div className="flex gap-2 bg-white rounded-lg border border-slate-200 p-2">
        <div className="flex-1 h-8 rounded-md bg-slate-50 px-3 flex items-center"><Typed text="South Yard" /></div>
        <div className="h-8 px-4 rounded-full bg-[#1E3A5F] text-white flex items-center gap-1 font-semibold text-[11px]"><Plus className="w-3 h-3" /> Add depot</div>
      </div>
    </Spot>
    <div className="grid grid-cols-3 gap-3">
      <DepotCard name="North Yard" jobs={12} logins={2} />
      <DepotCard name="South Yard" jobs={0} logins={0} fresh />
    </div>
  </MockShell>
);

const CREW = [["Jack Murray", "Full-time", "North Yard"], ["Liam O'Brien", "Full-time", "North Yard"], ["Sam Nguyen", "Casual", "South Yard"], ["Tom Kelly", "Full-time", "South Yard"]];
const Mini = ({ label, value }) => (
  <div><p className="text-[9px] font-semibold text-slate-500 mb-0.5">{label}</p><div className="h-7 rounded-md border border-slate-200 px-2 flex items-center text-[11px]">{value}</div></div>
);

const Crew = () => (
  <MockShell tab="Crew" depot="All depots">
    <p className="font-display font-bold text-[16px] mb-3">Crew</p>
    <div className="rounded-lg border border-slate-200 bg-white divide-y divide-slate-100">
      {CREW.map(([n, t, d]) => (
        <div key={n} className="flex items-center px-4 h-10 gap-3">
          <span className="w-6 h-6 rounded-full bg-slate-200 text-[9px] font-bold grid place-items-center">{n.split(" ").map((x) => x[0]).join("")}</span>
          <span className="font-semibold flex-1">{n}</span>
          <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${t === "Casual" ? "bg-orange-50 text-orange-700" : "bg-blue-50 text-[#1E3A5F]"}`}>{t}</span>
          <span className="w-20 text-slate-500 text-[11px]">{d}</span>
        </div>
      ))}
    </div>
    <div className="absolute inset-0 bg-slate-900/25" />
    <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: 0.2 }} className="absolute right-8 top-4 w-[290px]">
      <Spot on>
        <div className="rounded-xl bg-white shadow-2xl border border-slate-200 p-4 space-y-2.5">
          <p className="font-display font-bold text-[14px]">Add worker</p>
          <Mini label="Email" value={<Typed text="mia.russo@gmail.com" />} />
          <Mini label="Name" value="Mia Russo" />
          <div className="grid grid-cols-2 gap-1.5 text-[11px] font-semibold">
            <span className="h-7 rounded-md bg-[#EA580C]/10 border border-[#EA580C] text-[#C2410C] grid place-items-center">Casual</span>
            <span className="h-7 rounded-md border border-slate-200 grid place-items-center text-slate-500">Full-time</span>
          </div>
          <Mini label="Home depot" value="South Yard" />
          <div className="h-8 rounded-full bg-[#1E3A5F] text-white grid place-items-center text-[11px] font-semibold">Add to crew</div>
        </div>
      </Spot>
    </motion.div>
  </MockShell>
);

const DepotMenu = () => (
  <div className="relative">
    <DepotPill name="South Yard" />
    <motion.div initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
      className="absolute right-0 top-9 w-44 rounded-lg bg-white border border-slate-200 shadow-xl p-1 z-30">
      {["All depots", "North Yard", "South Yard"].map((n) => (
        <div key={n} className={`px-3 py-2 rounded-md text-[11px] flex items-center justify-between ${n === "South Yard" ? "bg-[#1E3A5F]/10 font-semibold text-[#1E3A5F]" : ""}`}>
          {n}{n === "South Yard" && <Check className="w-3 h-3" />}
        </div>
      ))}
    </motion.div>
  </div>
);

const Switch = () => (
  <MockShell tab="Overview" depotSlot={<Spot on><DepotMenu /></Spot>}>
    <OverviewBody />
  </MockShell>
);

const STEPS = [SignUp, Depots, Crew, Switch];
export const DepotScreens = ({ step }) => {
  const S = STEPS[step];
  return <S />;
};
